import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { MapPin } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { OFFICES, projectToSVG } from "../../lib/offices";

// Simplified world continent paths (low-poly outlines)
// Source: simplified from natural-earth-110m via mapshaper, viewBox 1000x500
const WORLD_PATH =
  "M158,93 L173,80 L194,79 L210,86 L228,80 L246,86 L260,80 L275,79 L286,86 L296,93 L305,100 L310,108 L300,118 L285,120 L270,116 L255,118 L242,124 L228,124 L213,128 L198,128 L184,124 L172,118 L162,108 Z M250,130 L280,128 L300,135 L320,140 L340,142 L360,138 L375,130 L390,128 L410,135 L425,145 L440,160 L450,178 L455,200 L450,225 L440,250 L425,275 L405,295 L380,310 L350,318 L320,315 L290,305 L265,290 L245,275 L230,255 L222,235 L220,215 L222,195 L228,175 L235,158 L243,145 Z M460,140 L490,135 L520,138 L545,148 L565,160 L580,178 L588,200 L590,225 L585,250 L575,275 L560,295 L540,310 L515,318 L490,318 L468,310 L450,295 L438,275 L432,250 L432,225 L438,200 L448,178 L455,158 Z M600,90 L640,85 L680,88 L715,95 L745,105 L770,120 L788,140 L795,165 L795,190 L788,215 L775,235 L755,250 L730,260 L700,265 L668,265 L638,260 L612,250 L590,235 L575,215 L568,190 L568,165 L575,140 L585,118 Z M785,160 L820,155 L855,160 L885,175 L905,195 L915,220 L915,245 L905,270 L885,290 L855,305 L820,310 L785,305 L755,290 L735,270 L725,245 L725,220 L735,195 L755,175 Z";

export const GlobalPresence = () => {
  const { t, lang } = useLanguage();
  const [hovered, setHovered] = useState(null);

  const markers = useMemo(
    () =>
      OFFICES.map((o) => {
        const { x, y } = projectToSVG(o.lat, o.lon);
        return { ...o, x, y };
      }),
    []
  );

  return (
    <section
      id="presence"
      data-testid="presence-section"
      className="relative bg-[#0D2F4C] text-white py-24 sm:py-32 overflow-hidden"
    >
      {/* Subtle dot grid background */}
      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "radial-gradient(circle, white 1px, transparent 1px)",
          backgroundSize: "30px 30px",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8">
        {/* Header */}
        <div className="max-w-3xl mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="flex items-center gap-4 mb-8"
          >
            <span className="h-px w-10 bg-white/60" />
            <span
              data-testid="presence-overline"
              className="text-xs uppercase tracking-[0.3em] font-bold text-white/80"
            >
              {t.presence.overline}
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            data-testid="presence-title"
            className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t.presence.title}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-white/70 text-lg leading-relaxed"
          >
            {t.presence.subtitle}
          </motion.p>
        </div>

        {/* SVG world map */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="relative w-full"
          data-testid="world-map-container"
        >
          <svg
            viewBox="0 0 1000 500"
            className="w-full h-auto"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Continents */}
            <path
              d={WORLD_PATH}
              fill="rgba(255,255,255,0.06)"
              stroke="rgba(255,255,255,0.18)"
              strokeWidth="0.8"
            />

            {/* Connection lines from HQ (Port Louis) */}
            {markers
              .filter((m) => !m.headquarters)
              .map((m) => {
                const hq = markers.find((x) => x.headquarters);
                return (
                  <line
                    key={`line-${m.key}`}
                    x1={hq.x}
                    y1={hq.y}
                    x2={m.x}
                    y2={m.y}
                    stroke="rgba(255,255,255,0.12)"
                    strokeWidth="0.5"
                    strokeDasharray="2 3"
                  />
                );
              })}

            {/* Office markers */}
            {markers.map((m, i) => {
              const labelX = m.x + (m.label_dx ?? 10);
              const labelY = m.y + (m.label_dy ?? -8);
              const anchor = m.label_anchor || "start";
              return (
                <g
                  key={m.key}
                  onMouseEnter={() => setHovered(m.key)}
                  onMouseLeave={() => setHovered(null)}
                  style={{ cursor: "pointer" }}
                  data-testid={`office-marker-${m.key}`}
                >
                  {/* Pulse */}
                  <circle cx={m.x} cy={m.y} r={m.headquarters ? 10 : 8}>
                    <animate
                      attributeName="r"
                      from={m.headquarters ? 6 : 5}
                      to={m.headquarters ? 18 : 14}
                      dur="2.5s"
                      begin={`${i * 0.2}s`}
                      repeatCount="indefinite"
                    />
                    <animate
                      attributeName="opacity"
                      from="0.55"
                      to="0"
                      dur="2.5s"
                      begin={`${i * 0.2}s`}
                      repeatCount="indefinite"
                    />
                  </circle>
                  {/* Core */}
                  <circle
                    cx={m.x}
                    cy={m.y}
                    r={m.headquarters ? 5 : 3.5}
                    fill="#FFFFFF"
                    stroke={m.headquarters ? "#FFFFFF" : "rgba(255,255,255,0.6)"}
                    strokeWidth={m.headquarters ? 2 : 1}
                  />

                  {/* Label background halo for legibility */}
                  <text
                    x={labelX}
                    y={labelY}
                    fontSize="11"
                    textAnchor={anchor}
                    stroke="#0D2F4C"
                    strokeWidth="3"
                    strokeLinejoin="round"
                    strokeOpacity="0.9"
                    style={{
                      fontFamily: "'Manrope', sans-serif",
                      fontWeight: m.headquarters ? 600 : 400,
                      paintOrder: "stroke",
                    }}
                  >
                    {m.name}
                  </text>
                  {/* Foreground label */}
                  <text
                    x={labelX}
                    y={labelY}
                    fontSize="11"
                    fill={hovered === m.key ? "#FFFFFF" : "rgba(255,255,255,0.92)"}
                    textAnchor={anchor}
                    style={{
                      fontFamily: "'Manrope', sans-serif",
                      fontWeight: m.headquarters ? 600 : 400,
                      transition: "fill 0.2s",
                    }}
                  >
                    {m.name}
                  </text>
                </g>
              );
            })}
          </svg>
        </motion.div>

        {/* Office list */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-16 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-white/10 border border-white/10"
          data-testid="office-list"
        >
          {OFFICES.map((o) => (
            <div
              key={o.key}
              data-testid={`office-card-${o.key}`}
              className="bg-[#0D2F4C] p-5 hover:bg-[#0A243A] transition-colors duration-300 cursor-default"
            >
              <div className="flex items-center gap-2 mb-1">
                <MapPin size={12} className="text-white/60" />
                <span className="text-sm font-medium text-white">
                  {o.name}
                  {o.headquarters && (
                    <span className="ml-2 text-[10px] uppercase tracking-wider text-white/60">
                      HQ
                    </span>
                  )}
                </span>
              </div>
              <div className="text-xs text-white/50 ml-5">
                {lang === "fr" ? o.country_fr : o.country_en}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
